import { useEffect, useMemo, useState } from 'react';
import { Card, Button, Form, Toast, ToastContainer } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';

type Day = { enabled: boolean; from: string; to: string };
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday'
};

const defaultHours = (): Record<string, Day> => {
  const o: Record<string, Day> = {};
  DAY_KEYS.forEach(k => {
    o[k] = { enabled: k !== 'sat' && k !== 'sun', from: '09:00', to: '17:00' };
  });
  return o;
};

const Availability = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const auth = token ? { Authorization: `Bearer ${token}` } : undefined;

  const browserTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    []
  );
  const zones: string[] = useMemo(() => {
    const sv = (Intl as any).supportedValuesOf;
    return typeof sv === 'function' ? sv('timeZone') : [browserTz, 'UTC'];
  }, [browserTz]);

  const [useHours, setUseHours] = useState(false);
  const [timezone, setTimezone] = useState(browserTz);
  const [hours, setHours] = useState<Record<string, Day>>(defaultHours());
  const [awayMessage, setAwayMessage] = useState(
    'We’re away right now — leave a message and we’ll reply by email.'
  );
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/account/widget-settings`, { headers: auth })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const s = d?.widget_settings || {};
        if (typeof s.useBusinessHours === 'boolean') setUseHours(s.useBusinessHours);
        if (s.timezone) setTimezone(s.timezone);
        if (s.awayMessage) setAwayMessage(s.awayMessage);
        if (s.businessHours) setHours({ ...defaultHours(), ...s.businessHours });
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setDay = (k: string, patch: Partial<Day>) =>
    setHours(prev => ({ ...prev, [k]: { ...prev[k], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/account/widget-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(auth || {}) },
        body: JSON.stringify({
          widget_settings: {
            useBusinessHours: useHours,
            timezone,
            businessHours: hours,
            awayMessage
          }
        })
      });
      setToast(
        res.ok ? { msg: 'Availability saved.', ok: true } : { msg: 'Could not save.', ok: false }
      );
    } catch {
      setToast({ msg: 'Could not save.', ok: false });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h2 className="mb-1 text-body-emphasis">Availability & office hours</h2>
      <p className="text-body-tertiary mb-4">
        Set when your team is available. Outside these hours the widget shows your away message and
        invites visitors to leave a message.
      </p>

      <Card style={{ maxWidth: 720 }}>
        <Card.Body className="d-flex flex-column gap-4">
          <Form.Check
            type="switch"
            label="Use office hours (otherwise availability follows agent online status)"
            checked={useHours}
            onChange={e => setUseHours(e.target.checked)}
          />

          <div style={{ maxWidth: 360 }}>
            <Form.Label className="fs-9">Timezone</Form.Label>
            <Form.Select value={timezone} onChange={e => setTimezone(e.target.value)}>
              {!zones.includes(timezone) && <option value={timezone}>{timezone}</option>}
              {zones.map(z => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className={useHours ? '' : 'opacity-50 pe-none'}>
            <h5 className="mb-3">Weekly hours</h5>
            <div className="d-flex flex-column gap-2">
              {DAY_KEYS.map(k => (
                <div key={k} className="d-flex align-items-center gap-3">
                  <Form.Check
                    style={{ width: 130 }}
                    label={DAY_LABELS[k]}
                    checked={hours[k].enabled}
                    onChange={e => setDay(k, { enabled: e.target.checked })}
                  />
                  <Form.Control
                    type="time"
                    style={{ width: 130 }}
                    value={hours[k].from}
                    disabled={!hours[k].enabled}
                    onChange={e => setDay(k, { from: e.target.value })}
                  />
                  <span className="text-body-tertiary">to</span>
                  <Form.Control
                    type="time"
                    style={{ width: 130 }}
                    value={hours[k].to}
                    disabled={!hours[k].enabled}
                    onChange={e => setDay(k, { to: e.target.value })}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Form.Label className="fs-9">Away message</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={awayMessage}
              onChange={e => setAwayMessage(e.target.value)}
            />
          </div>

          <div>
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          show={!!toast}
          onClose={() => setToast(null)}
          delay={2500}
          autohide
          bg={toast?.ok ? 'success' : 'danger'}
        >
          <Toast.Body className="text-white">{toast?.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default Availability;
